// src/App.js
import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import Layout from './components/Layout';
import Chatbot from './pages/Chatbot';
import Profile from './pages/Profile';
import PostDetailView from './components/PostDetailView';
import GroupPage from './pages/GroupPage';
import GroupChatPage from './pages/GroupChatPage';
import RecordsPage from './pages/RecordsPage';
import RecordDetailPage from './pages/RecordDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import RecordsLayout from './features/records/components/RecordsLayout';
import NoteView from './features/records/components/NoteView';
import TaskView from './features/records/components/TaskView';
import CalendarView from './features/records/components/CalendarView';
import MapView from './features/records/components/MapView';
import GalleryView from './features/records/components/GalleryView';
import GraphPlaceholderView from './features/records/components/GraphView/GraphPlaceholderView';
import NotificationHandler from './components/NotificationHandler';
import AnalyticsListener from './components/AnalyticsListener';
import { ensureFcmToken } from './services/pushService';
import { PostProvider } from './contexts/PostContext';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider } from './contexts/AuthContext';
import { CurrentUserProfileProvider } from './contexts/CurrentUserProfileContext';
import { FriendRequestsProvider } from './contexts/FriendRequestsContext';
import { GroupsProvider } from './features/groups';
import OnboardingGate from './components/OnboardingGate';
import { isNotificationSupported } from './utils/featureDetection';
import { getSecuredSearchClient } from './utils/secureSearch';
import { useUser, useTheme } from './hooks';
import { useActivityTracker } from './hooks/useActivityTracker';
import './App.css';

const AppShell = () => {
  const { user, isProfileIncomplete } = useUser();
  const [notificationSupported, setNotificationSupported] = useState(false);

  useTheme();
  useActivityTracker();

  // Legacy posts search: retained for backward compatibility with existing production data; remove once Records fully replace posts.
  useEffect(() => {
    if (isNotificationSupported()) {
      setNotificationSupported(true);
    } else {
      console.log('Notifications are not supported on this browser');
    }
  }, []);

  useEffect(() => {
    if (!user || !notificationSupported || isProfileIncomplete()) {
      return;
    }

    const initializeFCMInBackground = async () => {
      try {
        const token = await ensureFcmToken();
        console.log('FCM ensure token result:', token ? 'token saved' : 'no token');
      } catch (error) {
        console.error('Background FCM initialization failed:', error);
      }
    };

    initializeFCMInBackground();
  }, [user, notificationSupported, isProfileIncomplete]);

  useEffect(() => {
    if (!user || isProfileIncomplete()) {
      return;
    }

    const prefetchAlgoliaKey = async () => {
      try {
        await getSecuredSearchClient();
        console.log('Algolia secured key prefetched successfully');
      } catch (error) {
        console.error('Failed to prefetch Algolia key:', error);
      }
    };

    prefetchAlgoliaKey();
  }, [user, isProfileIncomplete]);

  return (
    <OnboardingGate>
      <GroupsProvider>
        <FriendRequestsProvider>
          {/* Legacy posts context: retained for backward compatibility with existing production data; remove once Records fully replace posts. */}
          <PostProvider>
            <Layout>
              <Outlet />
              {notificationSupported && <NotificationHandler />}
            </Layout>
          </PostProvider>
        </FriendRequestsProvider>
      </GroupsProvider>
    </OnboardingGate>
  );
};

// Root layout component
const RootLayout = () => {
  return (
    <ToastProvider>
      <AnalyticsListener />
      <div className="app-container">
        <AppShell />
      </div>
    </ToastProvider>
  );
};

// Playground route (assistant runtime inspector). Included in development
// builds, or in any build where REACT_APP_PLAYGROUND_ENABLED is set — both
// are inlined at build time, so the lazily imported playground chunk is
// eliminated from builds that opt out. Route availability grants no data
// authority: the backend projection independently requires an active grant.
const playgroundRoutes = [];
if (
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_PLAYGROUND_ENABLED === 'true'
) {
  const PlaygroundPage = React.lazy(() => import('./features/playground'));
  playgroundRoutes.push({
    path: "playground",
    element: (
      <React.Suspense fallback={null}>
        <PlaygroundPage />
      </React.Suspense>
    )
  });
}

// Create router configuration
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/groups" replace />
      },
      {
        path: "chat",
        element: <Chatbot />
      },
      {
        path: "profile",
        element: <Profile />
      },
      {
        path: "profile/friends",
        element: <Profile initialTab="friends" />
      },
      {
        path: "profile/:userId",
        element: <Profile />
      },
      {
        path: "profile/:userId/saved",
        element: <Profile initialTab="saved" />
      },
      // Legacy posts route: retained for backward compatibility with existing production data; remove once Records fully replace posts.
      {
        path: "post/:postId",
        element: <PostDetailView key={window.location.pathname} />
      },
      {
        path: "record/:recordId",
        element: <RecordDetailPage />
      },
      {
        path: "groups",
        element: <GroupPage />
      },
      {
        path: "notifications",
        element: <NotificationsPage />
      },
      {
        path: "groups/:groupId",
        element: <GroupChatPage />
      },
      {
        path: "records",
        element: <RecordsPage />,
        children: [
          {
            element: <RecordsLayout />,
            children: [
              {
                index: true,
                element: <Navigate to="notes" replace />
              },
              {
                path: "notes",
                element: <NoteView />
              },
              {
                path: "tasks",
                element: <TaskView />
              },
              {
                path: "calendar",
                element: <CalendarView />
              },
              {
                path: "map",
                element: <MapView />
              },
              {
                path: "gallery",
                element: <GalleryView />
              },
              {
                path: "graph",
                element: <GraphPlaceholderView />
              }
            ]
          }
        ]
      },
      ...playgroundRoutes,
      {
        path: "*",
        element: <Navigate to="/groups" replace />
      }
    ]
  }
]);

function App() {
  return (
    <AuthProvider>
      <CurrentUserProfileProvider>
        <RouterProvider router={router} />
      </CurrentUserProfileProvider>
    </AuthProvider>
  );
}

export default App;
